import { Dispatch, SetStateAction } from "react";
import { COLORS } from "../../constants/design/colors";
import {
  GetAccount200ResponsePositionsInner,
  OrderTypeResponse,
  SideResponse,
} from "../../codegen-api";
import { getTxExplorerUrl } from "../../utils/chain";
import {
  isValidDecimalInput,
  normalizeDecimalString,
  truncateDecimalString,
} from "../../utils/decimalString";
import { formatContractError } from "../../utils/errors";
import { alignPriceToStep } from "../../utils/format";
import { dec } from "../../utils/amount/decimal";
import {
  closeSizeForPercent as exactCloseSizeForPercent,
  heldAmount,
  resolveCloseSize,
} from "../../utils/amount/max/closePositionMax";
import { MarketSteps } from "../../utils/amount/spec";
import { IContractPriceStep } from "../../utils/instruments";
import { ToastEnum, ToastStatusEnum } from "../../utils/toast";
import { useGetAccount } from "../../hooks/api/account/useGetAccount";
import { usePerpOrder } from "../../hooks/api/order/usePerpOrder";
import { resolvePerpMarginMode } from "../../hooks/api/order/usePerpOrder.helpers";
import { useToast } from "../../hooks/toast";
import { useSFX } from "../../hooks/useSFX";
import { IBaseModalProps } from "../BaseModal";

type TranslateFn = (...args: any[]) => any;

export type IClosePrecision = Pick<
  IContractPriceStep,
  "amount_step" | "amount_precision" | "price_step" | "price_precision"
>;

/**
 * The side of `price_step` a limit close may round to. Closing a long SELLS, so
 * round up (never sell for less than entered); closing a short BUYS, so round
 * down (never pay more).
 */
export const closeLimitPriceDirection = (side?: SideResponse): "down" | "up" =>
  side === SideResponse.Buy ? "up" : "down";

/** A typed limit close price aligned to `price_step`, "" when it is not a price. */
export const alignCloseLimitPrice = (
  limitPriceInput: string,
  side: SideResponse | undefined,
  precision: IClosePrecision
): string =>
  alignPriceToStep(
    limitPriceInput,
    precision.price_step,
    precision.price_precision,
    closeLimitPriceDirection(side)
  );

/** The close field's grid, from the numeric precision the modal carries. */
export const closeSteps = (
  precision: Pick<IClosePrecision, "amount_step" | "amount_precision"> &
    Partial<Pick<IClosePrecision, "price_step" | "price_precision">>
): MarketSteps => {
  const amountStep = dec.parse(precision.amount_step) ?? "0";
  return {
    amountStep,
    priceStep: dec.parse(precision.price_step ?? 0) ?? "0",
    // A close is never held to the opening minimum: all of a position the
    // exchange opened must be closable.
    minOrderAmount: amountStep,
    amountPrecision: precision.amount_precision,
    pricePrecision: precision.price_precision ?? 2,
  };
};

/**
 * The size a 25/50/75/MAX button writes into the close field: MAX is the
 * position exactly (never floored — the exchange opened all of it, so all of it
 * is closable), every other stop is floored onto `amount_step`. Exact decimals
 * (P2 `closePositionMax`): the float version turned 50% of 0.58 into 0.28 and
 * read a short's negative amount as nothing to close.
 */
export const closeSizeForPercent = (
  positionAmount: string | number | undefined,
  pct: number,
  precision: Pick<IClosePrecision, "amount_step" | "amount_precision">
): string => {
  const size = exactCloseSizeForPercent(positionAmount, pct, closeSteps(precision));
  return dec.gt(size, "0") ? size : "";
};

/**
 * The close size to submit as plain decimal text, or `null` when the field does
 * not hold one (exponent text, a sign, zero, or less than one step). A size at
 * or above the position is the position's own amount (a full close) — compared
 * exactly: the old `1e-9` tolerance made a size one wei short a full close.
 */
export const resolvePerpCloseSize = (
  closeSizeInput: string,
  positionAmount: string | undefined,
  precision: Pick<IClosePrecision, "amount_step" | "amount_precision">
): string | null => {
  if (!isValidDecimalInput(closeSizeInput)) return null;
  return resolveCloseSize(closeSizeInput, positionAmount, closeSteps(precision));
};

/** Whether `closeSizeInput` asks for more than the position holds. */
export const closeSizeExceedsPosition = (
  closeSizeInput: string,
  positionAmount: string | number | undefined
): boolean => {
  const size = dec.parse(closeSizeInput);
  const held = heldAmount(positionAmount ?? null);
  return size !== null && (held === null || dec.gt(size, held));
};

type AccountData = ReturnType<typeof useGetAccount>["data"];
type MutateAccount = ReturnType<typeof useGetAccount>["mutate"];
type AddToast = ReturnType<typeof useToast>["addToast"];
type PlaySound = ReturnType<typeof useSFX>["playSound"];
type ClosePerpPosition = ReturnType<typeof usePerpOrder>["closePerpPosition"];
type PlacePerpOrder = ReturnType<typeof usePerpOrder>["placePerpOrder"];
type CancelRestingCloseOrders =
  ReturnType<typeof usePerpOrder>["cancelRestingCloseOrders"];

interface IRunExecutePerpCloseParams {
  position: GetAccount200ResponsePositionsInner | undefined;
  closeSizeInput: string;
  closeMode: "market" | "limit";
  accountData: AccountData;
  cancelRestingCloseOrders: CancelRestingCloseOrders;
  placePerpOrder: PlacePerpOrder;
  limitPriceInput: string;
  mutateAccount: MutateAccount;
  revalidateAfterPositionClose: () => void;
  onHide: IBaseModalProps["onHide"];
  playSound: PlaySound;
  addToast: AddToast;
  t: TranslateFn;
  apiError: TranslateFn;
  closePerpPosition: ClosePerpPosition;
  setError: Dispatch<SetStateAction<string | undefined>>;
  setLoading: Dispatch<SetStateAction<boolean>>;
}

export const runExecutePerpClose = async ({
  position,
  closeSizeInput,
  closeMode,
  accountData,
  cancelRestingCloseOrders,
  placePerpOrder,
  limitPriceInput,
  mutateAccount,
  revalidateAfterPositionClose,
  onHide,
  playSound,
  addToast,
  t,
  apiError,
  closePerpPosition,
  setError,
  setLoading,
}: IRunExecutePerpCloseParams) => {
  if (!position) {
    return;
  }
  // Only plain decimal text reaches `parseUnits`: "1e-7" made it throw, and an
  // empty or zero size used to be read as "close everything".
  const plainSize = isValidDecimalInput(closeSizeInput)
    ? normalizeDecimalString(closeSizeInput)
    : null;
  if (!plainSize || !(Number(plainSize) > 0)) {
    setError(
      t("invalid_close_size", { defaultValue: "Enter a valid size to close." })
    );
    return;
  }
  const plainLimitPrice = normalizeDecimalString(limitPriceInput);
  if (closeMode === "limit" && !(Number(plainLimitPrice) > 0)) {
    setError(
      t("invalid_limit_price", { defaultValue: "Enter a valid limit price." })
    );
    return;
  }
  try {
    setError(undefined);
    setLoading(true);

    // Exact: the whole position (as |amount| — a short's is negative) is a full
    // close; anything smaller, even by one wei, is partial.
    const held = heldAmount(position.amount ?? null);
    const fullClose = held !== null && dec.gte(plainSize, held);
    const sizeArg = fullClose ? undefined : truncateDecimalString(plainSize, 18);
    const closeSide =
      position.side === SideResponse.Buy
        ? SideResponse.Sell
        : SideResponse.Buy;

    if (closeMode === "limit") {
      // Perps create orders on-chain, never via REST. closePosition() is
      // market-only, so a limit close is an opposite-side LIMIT order through
      // the same path as a normal perp order (placePerpOrder/openPosition).
      // openPosition has no reduce_only flag, so we cap size to the position
      // (perpCloseValid) and let the engine net it against the open position.
      // Reuse the position's leverage/margin mode so the margin math mirrors it.
      const positionLeverage = (position as any).leverage;
      const sliderLeverage = accountData?.leverages?.find(
        (l) => l.instrument_id === position.instrument_id
      )?.leverage;
      const leverage = Number(positionLeverage ?? sliderLeverage) || 1;
      // No default: an unknown margin mode would place this reduce-only order
      // against the wrong leg when the trader holds both a cross and an
      // isolated position on this market and side.
      const marginMode = resolvePerpMarginMode(position);

      // Cancel any existing resting close orders first so the aggregate of
      // resting reduce orders can never exceed the position (which, without
      // on-chain reduce-only, could otherwise flip it into an opposite side).
      await cancelRestingCloseOrders(position);

      const res = await placePerpOrder({
        market: position.instrument_id,
        side: closeSide,
        order_type: OrderTypeResponse.Limit,
        // A full close is the held size, |amount|: a short's amount is
        // negative, and a negative size is not an order.
        size:
          sizeArg ??
          truncateDecimalString(held ?? "", 18) ??
          (held as string),
        // Aligned to `price_step` by the modal (`alignCloseLimitPrice`).
        price: plainLimitPrice as string,
        collateralAmount: "0", // ignored on-chain; margin derived from size/leverage
        leverage,
        marginMode,
        reduceOnly: true, // intent: only reduce. Enforced once enabled on-chain.
      });
      if (!res?.order_id) {
        throw new Error(t("close_position_error"));
      }
      mutateAccount();
      revalidateAfterPositionClose();
      onHide?.();
      playSound("order_placed", true);
      addToast(
        {
          type: ToastEnum.SIMPLE,
          header: t("limit_close_submitted_header", {
            defaultValue: "Limit close order placed",
          }),
          subheader: (
            <span>
              {t("limit_close_submitted_subheader", {
                defaultValue:
                  "Your limit order will close the position when the price is reached.",
              })}
              {res.tx_hash && (
                <>
                  {" "}
                  <a
                    href={getTxExplorerUrl(res.tx_hash)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: COLORS.link.one, textDecoration: "underline" }}
                  >
                    View transaction
                  </a>
                </>
              )}
            </span>
          ),
          status: ToastStatusEnum.SUCCESS,
        },
        4000
      );
      return;
    }

    // Market close (direct on-chain reduce-only).
    const result = await closePerpPosition(
      position,
      sizeArg ? { size: sizeArg } : undefined
    );
    const txHash = result?.tx_hash;

    // The row is hidden by the shared pending-close registry, which
    // `closePerpPosition` populated — see `usePositionsWSS`.
    //
    // This used to write an optimistic `/account` cache filtered on
    // `instrument_id` + `side` ALONE, which dropped both the cross and the
    // isolated leg of the same market and side, hiding a position the trader
    // still held. It also swept every trading endpoint a second time on its own
    // throttle ref, doubling the post-trade request burst, and re-fetched the
    // account again on a bare 2s timer.
    mutateAccount();
    onHide?.();
    playSound("settle", true);
    addToast(
      {
        type: ToastEnum.SIMPLE,
        header: t("position_closed_header"),
        subheader: (
          <span>
            {t("position_closed_subheader")}
            {txHash && (
              <>
                {" "}
                <a
                  href={getTxExplorerUrl(txHash)}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: COLORS.link.one, textDecoration: "underline" }}
                >
                  View transaction
                </a>
              </>
            )}
          </span>
        ),
        status: ToastStatusEnum.SUCCESS,
      },
      4000
    );
  } catch (createError: any) {
    const friendly = formatContractError(createError);
    setError(apiError(friendly) || friendly || t("close_position_error"));
  } finally {
    setLoading(false);
  }
};
